import { useEffect, useEffectEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import dayjs from "dayjs";
import { useHotelSearchDataContext } from "@/app/lib/context/HotelSearchDataContext";
import { TravelPurpose } from "@/app/lib/types/enums";
import PageLoader from "@/app/ui/PageLoader";
import AnimatedLoader from "@/app/ui/AnimatedLoader";

const hotelSearchSchema = z
  .object({
    checkIn: z.coerce
      .date()
      .refine((date) => !dayjs(date).isBefore(dayjs(), "day")),
    checkOut: z.coerce.date(),
    destinationId: z.coerce.string(),
    destinationName: z.string(),
    destinationCountry: z.string(),
    adults: fallback(z.coerce.number(), 1).default(1),
    travelPurpose: fallback(
      z.nativeEnum(TravelPurpose),
      TravelPurpose.LEISURE,
    ).default(TravelPurpose.LEISURE),
    utm_source: z.string().optional(),
    utm_campaign: z.string().optional(),
  })
  .refine((data) => dayjs(data.checkOut).isAfter(dayjs(data.checkIn), "day"), {
    path: ["checkOut"],
  });

export const Route = createFileRoute("/_authenticated/hotelsExternal")({
  component: RouteComponent,
  validateSearch: zodValidator(hotelSearchSchema),
});

function RouteComponent() {
  const {
    checkIn,
    checkOut,
    destinationId,
    destinationName,
    destinationCountry,
    adults,
    travelPurpose,
  } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { updateSearchData } = useHotelSearchDataContext();

  const setSearchData = useEffectEvent(() => {
    updateSearchData({
      destination: {
        id: destinationId,
        name: destinationName,
        countryName: destinationCountry,
        type: "CITY",
      },
      dates: {
        checkIn,
        checkOut,
      },
      rooms: [{ adults, children: 0, childrenAges: [] }],
      travelPurpose,
    });

    navigate({ to: "/hotels/list", replace: true });
  });

  useEffect(() => {
    setSearchData();
  }, []);

  return (
    <PageLoader>
      <AnimatedLoader icon="buildings" />
    </PageLoader>
  );
}
